import React, { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { useRecaptcha } from "@/hooks/useRecaptcha";
import { toast } from "sonner";
import { Mail, MapPin, Clock, FileText, MessageCircle, CheckCircle2, ShieldCheck } from "lucide-react";

export default function Contact() {
  const { t } = useLang();
  const { execute } = useRecaptcha();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const recaptcha_token = await execute("contact_form_submit");
      await api.post("/contact", { ...form, recaptcha_token, recaptcha_action: "contact_form_submit" });
      setSent({ ...form });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-none border border-slate-300 bg-white px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div data-testid="contact-page">
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h1 className="text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">{t("contact.title")}</h1>
          <p className="mt-4 max-w-2xl text-base text-slate-600">{t("contact.subtitle")}</p>
        </div>
      </section>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-12 sm:px-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {sent ? (
            <div className="border border-green-200 bg-green-50 p-8" data-testid="contact-success">
              <CheckCircle2 className="mb-4 h-12 w-12 text-green-600" />
              <h2 className="text-2xl font-bold tracking-tight text-slate-950">{t("contact.successTitle")}</h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-700">{t("contact.successBody")}</p>
              <div className="mt-6 border-l-2 border-green-500 bg-white p-4">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("contact.recap")}</div>
                <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{sent.message}</p>
              </div>
              <button onClick={() => setSent(null)} className="mt-6 rounded-none border border-slate-900 px-6 py-3 text-sm font-bold uppercase tracking-widest text-slate-900 transition-colors hover:bg-slate-900 hover:text-white" data-testid="contact-send-another">
                {t("contact.sendAnother")}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">{t("contact.name")}</label>
                  <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="contact-name" />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">{t("contact.email")}</label>
                  <input required type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-email" />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">{t("contact.message")}</label>
                <textarea required rows={6} value={form.message} onChange={update("message")} className={inputCls} data-testid="contact-message" />
              </div>
              <button type="submit" disabled={sending} className="rounded-none bg-blue-700 px-8 py-3 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-blue-800 disabled:opacity-60" data-testid="contact-submit-btn">
                {sending ? "…" : t("common.send")}
              </button>
              <p className="flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> {t("contact.protected")}</p>
            </form>
          )}
        </div>

        <div className="space-y-6 border border-slate-200 bg-white p-8" data-testid="contact-info">
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-950">{t("contact.infoTitle")}</h3>
          <div className="flex items-start gap-3"><MapPin className="mt-0.5 h-5 w-5 text-blue-700" /><div><div className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("contact.address")}</div><div className="text-sm text-slate-700">equi-tec di Daniele Reggi<br />via Canaletto 25/3, 48022 Lugo (RA), Italia</div></div></div>
          <div className="flex items-start gap-3"><Mail className="mt-0.5 h-5 w-5 text-blue-700" /><div><div className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("contact.emailLabel")}</div><div className="text-sm text-slate-700">info@equi-tec.com</div></div></div>
          <a href="https://wa.me/393345742549" target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 transition-colors hover:text-green-700" data-testid="contact-whatsapp"><MessageCircle className="mt-0.5 h-5 w-5 text-green-600" /><div><div className="text-xs font-bold uppercase tracking-wider text-slate-500">WhatsApp / {t("contact.phone")}</div><div className="text-sm font-medium text-slate-900">+39 334 574 2549</div><div className="text-xs text-slate-500">{t("contact.phoneNote")}</div></div></a>
          <div className="flex items-start gap-3"><FileText className="mt-0.5 h-5 w-5 text-blue-700" /><div><div className="text-xs font-bold uppercase tracking-wider text-slate-500">P.IVA / VAT</div><div className="text-sm text-slate-700">02685120392</div></div></div>
          <div className="flex items-start gap-3"><Clock className="mt-0.5 h-5 w-5 text-blue-700" /><div><div className="text-xs font-bold uppercase tracking-wider text-slate-500">Hours</div><div className="text-sm text-slate-700">{t("contact.hours")}</div></div></div>
        </div>
      </div>
    </div>
  );
}
